import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { 
  ArrowLeft, 
  Settings, 
  Signal,
  SignalLow,
  SignalMedium,
  SignalHigh,
  Volume2,
  VolumeX,
  Footprints,
  ListChecks,
  ChevronRight,
  X,
  Navigation
} from "lucide-react";
import { SAFE_AREA } from "@/utils/safeArea";
import { PlatformMap } from "@/components/PlatformMap";
import logoLight from "@/assets/straiv-logo-light.png";
import logoDark from "@/assets/straiv-logo-dark.png";
import { useTheme } from "next-themes";
import { Geolocation } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import { TextToSpeech } from "@capacitor-community/text-to-speech";
import { getTTSVoiceIndex } from "@/lib/ttsVoice";
import { toast } from "sonner";
import { App } from "@capacitor/app";
import { ensureLocationForOutdoorRun, openLocationSettings } from "@/lib/runLocationPermission";
import { prepareAudioForRun } from "@/lib/straivNowPlaying";
import { LocationPermissionDialog } from "@/components/LocationPermissionDialog";
import type { LocationPermissionReason } from "@/components/LocationPermissionDialog";
import { Analytics } from "@/lib/analytics";

type ActivityType = 'outdoor' | 'treadmill';
type AudioStatsInterval = '1min' | '5min' | '10min' | '500m' | '1km' | '5km' | 'off';
type AudioVoice = 'female' | 'male';

const TIME_INTERVALS: AudioStatsInterval[] = ['1min', '5min', '10min'];
const DISTANCE_INTERVALS: AudioStatsInterval[] = ['500m', '1km', '5km'];

const formatAudioIntervalLabel = (v: AudioStatsInterval): string => {
  if (v === 'off') return 'Off';
  if (v.endsWith('min')) return v.replace('min', ' min');
  return v; // 500m, 1km, 5km
};

export const RunningStart = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  // Settings state - load from localStorage
  const [activityType, setActivityType] = useState<ActivityType>('outdoor');
  const [audioStats, setAudioStats] = useState<AudioStatsInterval>('5min');
  const [audioVoice, setAudioVoice] = useState<AudioVoice>('female');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showWorkouts, setShowWorkouts] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState<{ title: string; description: string } | null>(null);
  
  // GPS state
  const [currentPosition, setCurrentPosition] = useState<[number, number] | null>(null);
  const [gpsSignal, setGpsSignal] = useState<'strong' | 'medium' | 'weak' | 'none'>('none');
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [positionRefreshKey, setPositionRefreshKey] = useState(0);
  
  // Load saved settings on mount and log screen view
  useEffect(() => {
    // Log screen opened
    Analytics.runningScreenOpened();
    Analytics.logScreenView('RunningStart');
    
    // Prepare audio session early so music keeps playing when user taps Start
    prepareAudioForRun();
    
    const savedSettings = localStorage.getItem('runningUserSettings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        if (settings.activityType) setActivityType(settings.activityType);
        if (settings.audioStats) setAudioStats(settings.audioStats);
        if (settings.audioVoice) setAudioVoice(settings.audioVoice);
      } catch (e) {
        console.warn('Failed to load saved settings:', e);
      }
    }
  }, []);
  
  // Save settings when they change
  useEffect(() => {
    const settings = { activityType, audioStats, audioVoice };
    localStorage.setItem('runningUserSettings', JSON.stringify(settings));
  }, [activityType, audioStats, audioVoice]);
  
  // Request location permission and get position on mount (and when returning from Settings)
  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) return;
    
    let isMounted = true;
    let watchId: string | null = null;
    const isRefresh = positionRefreshKey > 0;
    
    const getLocation = async () => {
      try {
        // On refresh (returned from Settings), skip request - we know they granted
        if (!isRefresh) {
          Analytics.runningGpsPermissionRequested();
          const permission = await Geolocation.requestPermissions();
          if (permission.location !== 'granted') {
            Analytics.runningGpsPermissionDenied();
            if (isMounted) {
              setPermissionDialog({ open: true, reason: 'denied' });
              setGpsSignal('none');
            }
            return;
          }
          Analytics.runningGpsPermissionGranted();
        } else {
          const permission = await Geolocation.checkPermissions();
          if (permission.location !== 'granted') return;
        }
        
        // Try to get a quick low-accuracy position first for instant feedback
        try {
          const quickPosition = await Geolocation.getCurrentPosition({
            enableHighAccuracy: false, // Faster, less accurate
            timeout: 3000, // 3 second timeout for quick fix
            maximumAge: 30000, // Accept cached position up to 30 seconds old
          });
          
          if (isMounted && quickPosition) {
            setCurrentPosition([quickPosition.coords.latitude, quickPosition.coords.longitude]);
            setGpsAccuracy(quickPosition.coords.accuracy);
            setGpsSignal('weak'); // Mark as weak since it's low accuracy
          }
        } catch (quickError) {
          console.log('Quick position not available, waiting for high accuracy...');
        }
        
        // Now get high-accuracy position
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000, // Reduced from 15s to 10s
          maximumAge: 5000, // Accept position up to 5 seconds old
        });
        
        if (isMounted && position) {
          setCurrentPosition([position.coords.latitude, position.coords.longitude]);
          setGpsAccuracy(position.coords.accuracy);
          
          // Set signal strength based on accuracy
          let signalStrength: 'strong' | 'medium' | 'weak' | 'none' = 'none';
          if (position.coords.accuracy <= 5) {
            signalStrength = 'strong';
          } else if (position.coords.accuracy <= 15) {
            signalStrength = 'medium';
          } else if (position.coords.accuracy <= 30) {
            signalStrength = 'weak';
          }
          setGpsSignal(signalStrength);
          
          // Log GPS signal acquired
          Analytics.runningGpsSignalAcquired(position.coords.accuracy, signalStrength);
        }
        
        // Continue watching for better accuracy
        watchId = await Geolocation.watchPosition(
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          },
          (pos, err) => {
            if (err || !pos || !isMounted) return;
            
            setCurrentPosition([pos.coords.latitude, pos.coords.longitude]);
            setGpsAccuracy(pos.coords.accuracy);
            
            if (pos.coords.accuracy <= 5) {
              setGpsSignal('strong');
            } else if (pos.coords.accuracy <= 15) {
              setGpsSignal('medium');
            } else if (pos.coords.accuracy <= 30) {
              setGpsSignal('weak');
            } else {
              setGpsSignal('none');
            }
          }
        );
      } catch (error) {
        console.warn('Failed to get location:', error);
        if (isMounted) {
          setGpsSignal('none');
        }
      }
    };
    
    getLocation();
    
    return () => {
      isMounted = false;
      if (watchId) {
        Geolocation.clearWatch({ id: watchId }).catch(console.warn);
      }
    };
  }, [positionRefreshKey]);
  
  // Arnold's running workouts data
  const runningWorkouts = [
    {
      title: "Interval Training",
      description: `Warm up: 5 min easy jog

Main set: 8 x (1 min fast, 1 min recovery)

Cool down: 5 min easy jog

Total: ~26 minutes

This workout improves your speed and cardiovascular fitness by alternating between high-intensity bursts and recovery periods.`
    },
    {
      title: "Easy Recovery Run",
      description: `Duration: 20-30 minutes at a conversational pace.

Keep heart rate in Zone 2.

Focus on form and breathing.

This run helps your body recover from harder sessions while maintaining your aerobic base.`
    },
    {
      title: "Tempo Run",
      description: `Warm up: 10 min easy

Tempo: 20 min at "comfortably hard" pace

Cool down: 10 min easy

Total: ~40 minutes

Tempo runs improve your lactate threshold, allowing you to run faster for longer periods.`
    },
    {
      title: "Long Slow Distance",
      description: `Duration: 45-60 minutes at an easy pace.

Build your aerobic base.

Stay hydrated and enjoy the run!

Long runs are the foundation of endurance training, building mental and physical stamina.`
    },
    {
      title: "Sprint Training",
      description: `Warm up: 10 min easy jog + dynamic stretches

Main set: 6 x 100m sprints (walk back recovery)

Cool down: 5 min easy jog

Great for building speed and power! Focus on explosive starts and maintaining form throughout each sprint.`
    },
  ];
  
  // Unlock audio session in user gesture context (required for iOS - TTS won't work otherwise)
  const unlockAudioSession = () => {
    try {
      if (typeof window === 'undefined') return;
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        ctx.resume?.(); // iOS starts suspended
        const buf = ctx.createBuffer(1, 128, ctx.sampleRate);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
      }
    } catch {}
  };

  const [isStarting, setIsStarting] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownNumber, setCountdownNumber] = useState<number | 'go'>(3);
  const pendingNavigateRef = useRef<'outdoor' | 'treadmill' | null>(null);
  const [permissionDialog, setPermissionDialog] = useState<{
    open: boolean;
    reason: LocationPermissionReason;
  }>({ open: false, reason: "notGranted" });
  const permissionDialogRef = useRef(permissionDialog);
  permissionDialogRef.current = permissionDialog;

  // Re-check permission when app returns from Settings - close if Always, or update dialog to current reason
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return;
    const sub = App.addListener("appStateChange", async ({ isActive }) => {
      if (!isActive || !permissionDialogRef.current.open) return;
      // Delay so iOS has updated CLLocationManager.authorizationStatus after returning from Settings
      await new Promise((r) => setTimeout(r, 800));
      const result = await ensureLocationForOutdoorRun();
      if (result.ok) {
        setPermissionDialog({ open: false, reason: "notGranted" });
        setPositionRefreshKey((k) => k + 1);
      } else {
        const failResult = result as { ok: false; reason: LocationPermissionReason };
        setPermissionDialog({ open: true, reason: failResult.reason });
        if (failResult.reason === "whenInUse") {
          setPositionRefreshKey((k) => k + 1);
        }
      }
    });
    return () => {
      sub.then((l) => l.remove());
    };
  }, []);

  // Countdown timer: 3, 2, 1, Go - visual + optional TTS (when audio not off)
  useEffect(() => {
    if (!showCountdown || !pendingNavigateRef.current) return;
    let cancelled = false;
    const target = pendingNavigateRef.current;
    const runCountdown = async () => {
      const steps: { n: number | 'go'; text: string }[] = [
        { n: 3, text: '3' },
        { n: 2, text: '2' },
        { n: 1, text: '1' },
        { n: 'go', text: 'Go!' },
      ];
      const useCountdownAudio = audioStats !== 'off' && Capacitor.isNativePlatform();
      const voiceIndex = useCountdownAudio ? await getTTSVoiceIndex(audioVoice) : undefined;
      for (const { n, text } of steps) {
        if (cancelled) return;
        setCountdownNumber(n);
        if (useCountdownAudio) {
          try {
            const speakOpts: Record<string, unknown> = {
              text,
              lang: 'en-US',
              rate: 0.95,
              pitch: audioVoice === 'male' ? 0.9 : 1.05,
              volume: 1.0,
              queueStrategy: 1,
              ...(voiceIndex !== undefined && voiceIndex >= 0 && { voice: voiceIndex }),
            };
            if (Capacitor.getPlatform() === 'ios') {
              speakOpts.category = 'playback';
              speakOpts.duckOthers = true;
            }
            await TextToSpeech.speak(speakOpts as unknown as Parameters<typeof TextToSpeech.speak>[0]);
          } catch {}
        }
        const delayAfterStep = n === 'go' ? 0 : (useCountdownAudio ? 400 : 600);
        await new Promise((r) => setTimeout(r, delayAfterStep));
        if (cancelled) return;
      }
      pendingNavigateRef.current = null;
      setShowCountdown(false);
      setCountdownNumber(3);
      const startTime = new Date().toISOString();
      const settings = { activityType, audioStats, audioVoice, startTime };
      localStorage.setItem('runningSettings', JSON.stringify(settings));
      if (target === 'outdoor') {
        // Location already checked before countdown - just navigate
        navigate('/running-active');
      } else {
        navigate('/running-workout');
      }
    };
    runCountdown();
    return () => {
      cancelled = true;
      pendingNavigateRef.current = null;
    };
  }, [showCountdown, navigate, audioStats, audioVoice]);

  const handleStart = async () => {
    prepareAudioForRun(); // Set ambient + mixWithOthers before any audio - avoids music pause
    // Skip unlockAudioSession - Web Audio activation pauses music; TTS works without it
    const startTime = new Date().toISOString();
    const settings = { activityType, audioStats, audioVoice, startTime };
    localStorage.setItem('runningSettings', JSON.stringify(settings));
    Analytics.runningWorkoutStarted(activityType === 'outdoor', {
      activity_type: activityType,
      audio_interval: audioStats,
      audio_voice: audioVoice,
      gps_signal: gpsSignal,
      gps_accuracy: gpsAccuracy
    });
    if (activityType === 'outdoor') {
      // Check "Always" location BEFORE countdown - don't start countdown if not ready
      const result = await ensureLocationForOutdoorRun();
      if (!result.ok) {
        const failResult = result as { ok: false; reason: import('@/lib/runLocationPermission').LocationPermissionReason };
        setPermissionDialog({ open: true, reason: failResult.reason });
        return;
      }
      pendingNavigateRef.current = 'outdoor';
      setShowCountdown(true);
    } else {
      // Indoor: no countdown, go straight to treadmill workout
      navigate('/running-workout');
    }
  };

  const getSignalIcon = () => {
    if (gpsSignal === 'strong') return <SignalHigh className="h-5 w-5 text-primary" />;
    if (gpsSignal === 'medium') return <SignalMedium className="h-5 w-5 text-amber-500" />;
    if (gpsSignal === 'weak') return <SignalLow className="h-5 w-5 text-orange-500" />;
    return <Signal className="h-5 w-5 text-destructive" />;
  };

  const getSignalBars = () => {
    const bars = gpsSignal === 'strong' ? 4 : gpsSignal === 'medium' ? 3 : gpsSignal === 'weak' ? 2 : 1;
    return (
      <div className="flex items-end gap-0.5 h-4">
        {[1, 2, 3, 4].map((bar) => (
          <div
            key={bar}
            className={`w-1 rounded-sm transition-all ${
              bar <= bars 
                ? 'bg-primary' 
                : 'bg-muted-foreground/30'
            }`}
            style={{ height: `${bar * 25}%` }}
          />
        ))}
      </div>
    );
  };

  // Map center state - separate from current position so map doesn't refresh constantly
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [isMapCentered, setIsMapCentered] = useState(true); // Track if map is centered on user
  
  const lastProcessedRefreshKeyRef = useRef(0);
  // Set/update map center when position is available (including once per refresh from Settings)
  useEffect(() => {
    if (!currentPosition) return;
    if (!mapCenter) {
      setMapCenter(currentPosition);
    } else if (positionRefreshKey > lastProcessedRefreshKeyRef.current) {
      setMapCenter([...currentPosition]);
      lastProcessedRefreshKeyRef.current = positionRefreshKey;
    }
  }, [currentPosition, mapCenter, positionRefreshKey]);
  
  // Re-center map to current position
  const handleRecenterMap = () => {
    if (currentPosition) {
      setMapCenter([...currentPosition]);
    }
    setIsMapCentered(true); // Show dot, hide recenter button
    Analytics.runningMapRecentered();
  };
  
  // Handle map interaction (user scrolled/panned away)
  const handleMapInteraction = () => {
    setIsMapCentered(false); // Hide dot, show recenter button
    Analytics.runningMapScrolled();
  };
  
  // Use mapCenter for map display, fallback to currentPosition or default
  const mapLat = mapCenter ? mapCenter[0] : (currentPosition ? currentPosition[0] : 59.3293);
  const mapLng = mapCenter ? mapCenter[1] : (currentPosition ? currentPosition[1] : 18.0686);
  
  return (
    <div className="fixed inset-0 bg-background flex flex-col">
      {/* Map Background - Apple Maps with user location marker */}
      <div className="absolute inset-0 z-0">
        <PlatformMap 
          key={positionRefreshKey > 0 ? `map-refresh-${positionRefreshKey}` : 'map-initial'}
          center={mapCenter || currentPosition}
          userLocation={currentPosition}
          showUserMarker={true}
          className="w-full h-full"
          darkMode={true}
          autoFollow={isMapCentered}
          onUserScroll={handleMapInteraction}
        />
        
        {/* Gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/30 pointer-events-none z-20" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background/60 to-transparent pointer-events-none z-20" />
      </div>
      
      {/* Settings Sheet */}
      <Sheet open={settingsOpen} onOpenChange={(open) => {
        setSettingsOpen(open);
        if (open) Analytics.runningSettingsOpened();
      }}>
          <SheetContent side="bottom" className="rounded-t-3xl pb-8">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-center">Run Settings</SheetTitle>
            </SheetHeader>
            
            <div className="space-y-1">
              {/* Activity Type */}
              <div className="flex items-center justify-between py-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                  <Footprints className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Activity</p>
                    <p className="font-semibold">Running</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={activityType === 'outdoor' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setActivityType('outdoor');
                      Analytics.runningActivityTypeChanged('outdoor');
                    }}
                    className={`text-xs ${activityType === 'outdoor' ? 'bg-primary text-primary-foreground' : ''}`}
                  >
                    Outdoor
                  </Button>
                  <Button
                    variant={activityType === 'treadmill' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setActivityType('treadmill');
                      Analytics.runningActivityTypeChanged('treadmill');
                    }}
                    className={`text-xs ${activityType === 'treadmill' ? 'bg-primary text-primary-foreground' : ''}`}
                  >
                    Treadmill
                  </Button>
                </div>
              </div>
              
              {/* Show Workouts */}
              <div 
                className="flex items-center justify-between py-4 border-b border-border/50 cursor-pointer"
                onClick={() => {
                  setShowWorkouts(true);
                  Analytics.runningWorkoutsListOpened();
                }}
              >
                <div className="flex items-center gap-3">
                  <ListChecks className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Show Workouts</p>
                    <p className="font-semibold">AI Running Plans</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
              
              {/* Audio Stats Interval - time OR distance (mutually exclusive) */}
              <div className="flex items-center justify-between py-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                  {audioStats === 'off' ? (
                    <VolumeX className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <Volume2 className="h-5 w-5 text-primary" />
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Audio Interval</p>
                    <p className="font-semibold">{formatAudioIntervalLabel(audioStats)}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground shrink-0 w-14">By time</span>
                    {TIME_INTERVALS.map((interval) => (
                      <Button
                        key={interval}
                        variant={audioStats === interval ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => {
                          setAudioStats(interval);
                          Analytics.runningAudioIntervalChanged(interval);
                        }}
                        className={`text-xs px-2 ${audioStats === interval ? 'bg-primary text-primary-foreground' : ''}`}
                      >
                        {interval}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground shrink-0 w-14">By distance</span>
                    {DISTANCE_INTERVALS.map((interval) => (
                      <Button
                        key={interval}
                        variant={audioStats === interval ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => {
                          setAudioStats(interval);
                          Analytics.runningAudioIntervalChanged(interval);
                        }}
                        className={`text-xs px-2 ${audioStats === interval ? 'bg-primary text-primary-foreground' : ''}`}
                      >
                        {interval}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant={audioStats === 'off' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setAudioStats('off');
                        Analytics.runningAudioIntervalChanged('off');
                      }}
                      className={`text-xs px-2 ${audioStats === 'off' ? 'bg-primary text-primary-foreground' : ''}`}
                    >
                      Off
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Audio Voice */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <Volume2 className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Voice</p>
                    <p className="font-semibold capitalize">{audioVoice}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {(['female', 'male'] as AudioVoice[]).map((voice) => (
                    <Button
                      key={voice}
                      variant={audioVoice === voice ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setAudioVoice(voice);
                        Analytics.runningAudioVoiceChanged(voice);
                      }}
                      className={`text-xs px-2 capitalize ${audioVoice === voice ? 'bg-primary text-primary-foreground' : ''}`}
                    >
                      {voice}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
        
        {/* Show Workouts Sheet */}
        <Sheet open={showWorkouts} onOpenChange={setShowWorkouts}>
          <SheetContent side="bottom" className="h-[80vh] rounded-t-3xl [&>button]:hidden">
            <SheetHeader className="pb-4 relative">
              <button
                onClick={() => setShowWorkouts(false)}
                className="absolute right-0 top-0 p-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
              <SheetTitle className="text-xl text-center">AI Running Workouts</SheetTitle>
            </SheetHeader>
            
            <div className="space-y-3 overflow-y-auto max-h-[calc(80vh-100px)]">
              {runningWorkouts.map((workout, idx) => (
                <div 
                  key={idx}
                  className="p-4 bg-muted/30 rounded-xl border border-border/30 cursor-pointer hover:bg-muted/50 transition-colors active:scale-[0.98]"
                  onClick={() => {
                    setSelectedWorkout(workout);
                    Analytics.runningWorkoutSelected(workout.title);
                  }}
                >
                  <h3 className="font-semibold text-base">{workout.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {workout.description.split('\n')[0]}...
                  </p>
                </div>
              ))}
              
              <p className="text-xs text-center text-muted-foreground py-4">
                Ask your AI coach for personalized running workouts in the chat!
              </p>
            </div>
          </SheetContent>
        </Sheet>
        
        {/* Selected Workout Detail Sheet */}
        <Sheet open={!!selectedWorkout} onOpenChange={(open) => !open && setSelectedWorkout(null)}>
          <SheetContent side="bottom" className="h-[70vh] rounded-t-3xl [&>button]:hidden">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-xl text-center">{selectedWorkout?.title}</SheetTitle>
            </SheetHeader>
            
            <div className="overflow-y-auto max-h-[calc(70vh-120px)]">
              <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">
                {selectedWorkout?.description}
              </p>
            </div>
            
            <div className="pt-4">
              <Button 
                onClick={() => setSelectedWorkout(null)}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </SheetContent>
        </Sheet>

      {/* Top UI (settings button only - no exit button) */}
      <div className="relative z-10 px-4" style={{ paddingTop: `calc(${SAFE_AREA.top} + 12px)` }}>
        {/* Logo centered */}
        <div className="flex items-center justify-center py-3">
          <img
            src={theme === "dark" ? logoDark : logoLight}
            alt="STRAIV"
            className="h-8 object-contain"
          />
        </div>

        {/* Settings button (always visible) */}
        <div className="pb-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSettingsOpen(true)}
            className="h-11 w-11 rounded-full bg-background/90 backdrop-blur-md shadow-lg border border-border/30"
          >
            <Settings className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />
      
      {/* Bottom Section */}
      <div className="relative z-10 px-4 pb-4" style={{ paddingBottom: `calc(${SAFE_AREA.bottom} + 80px)` }}>
        {/* GPS Signal and Re-center - Above Start button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center px-3 py-2 rounded-full bg-background/90 backdrop-blur-md shadow-md border border-border/30">
            {getSignalBars()}
          </div>
          
          {/* Re-center map button - only visible when user has scrolled away */}
          {currentPosition && !isMapCentered && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRecenterMap}
              className="h-10 w-10 rounded-full bg-primary/90 backdrop-blur-md shadow-lg border border-border/30 animate-in fade-in-0 zoom-in-95 duration-200"
            >
              <Navigation className="h-4 w-4 text-primary-foreground" />
            </Button>
          )}
        </div>
        
        {/* Start Button - Same style as Log Gym Workout */}
        <Button
          onClick={handleStart}
          disabled={isStarting}
          className="w-full h-14 text-lg font-bold bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all duration-300 rounded-2xl disabled:opacity-70"
        >
          {isStarting ? "Starting…" : "Start"}
        </Button>
      </div>

      {/* Countdown overlay: 3, 2, 1, Go! */}
      {showCountdown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-md">
          <div className="text-[min(25vw,120px)] font-bold tabular-nums text-primary animate-in zoom-in-50 duration-300">
            {countdownNumber === 'go' ? 'Go!' : countdownNumber}
          </div>
        </div>
      )}

      {/* Location permission dialog - shown when run blocked due to missing Always permission */}
      <LocationPermissionDialog
        open={permissionDialog.open}
        onOpenChange={(open) => setPermissionDialog((p) => ({ ...p, open }))}
        reason={permissionDialog.reason}
        onOpenSettings={openLocationSettings}
      />
    </div>
  );
};
